'use client';

import { Vehicle, type Vendor } from '@cruz-com/lookup';
import dayjs from 'dayjs';
import _ from 'lodash';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { myCruzApi } from '@/api';
import { Bullet, CardBody, CardItem, CardPhoto, CruzIcon, Divider } from '@/components';
import { useGlobalStore } from '@/store/provider';
import { DealerInfo } from './dealerInfo';
import { EstimatedValue } from './estimatedValue';
import { Mileage } from './mileage';
import { MPG } from './mpg';
import { Price } from './price';
import { UserInfo } from './userInfo';
type Props = {
  v: Vehicle.Summary | Partial<Vehicle.Detail>;
  showUser?: boolean;
  showStockNumber?: boolean;
  showAcceleration?: boolean;
  showEvRange?: boolean;
  showPhotoCount?: boolean;
  hideLead?: boolean;
  ignoreKeys?: string[];
  onUnSavedAction?: () => void;
  unSaveOnly?: boolean;
  showDateSaved?: boolean;
  ToggleCompare?: React.ReactNode;
};
export function VehicleCard({
  v,
  showUser,
  showStockNumber,
  showAcceleration,
  showEvRange,
  showPhotoCount,
  hideLead,
  ignoreKeys,
  onUnSavedAction,
  unSaveOnly,
  showDateSaved,
  ToggleCompare
}: Props) {
  const {
    user,
    setUser,
    setModalSignIn
  } = useGlobalStore(state => state);
  const [isSaving, setIsSaving] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const Acceleration = () => {
    const acc = v?.specification?.acceleration;
    if (!acc) return '-';
    return <>
        {acc} <span className='text-xs font-light'>seconds</span>
      </>;
  };
  const EvRange = () => {
    const d = v?.engine?.miles_per_charge;
    if (!d) return '-';
    return <>
        {d} <span className='text-xs font-light'>miles</span>
      </>;
  };

  // save vehicle
  const handleSaveVehicle = async () => {
    if (_.isEmpty(user)) {
      setModalSignIn({
        show: true,
        saveVehicleId: v._id as string
      });
      return;
    }
    try {
      setIsSaving(true);
      const {
        data
      } = await myCruzApi.saveVehicle.toggleSave(v._id as string);
      setUser(data, null);
      if (onUnSavedAction) {
        onUnSavedAction();
      }
    } finally {
      setIsSaving(false);
    }
  };
  const SaveVehicle = () => {
    if (!_.isEmpty(user) && user?._id === v?.user_id) return null;

    // Derived state for screen reader text
    const isSavedStatus = unSaveOnly || _.includes(user?.saved_vehicle_ids, v._id);
    return <button type='button' className='btn-save-vehicle' onClick={handleSaveVehicle} aria-pressed={isSavedStatus} aria-label={isSavedStatus ? 'Unsave Vehicle' : 'Save Vehicle'}>
        {isSaving ? <CruzIcon.Spin /> : isSavedStatus ? <CruzIcon.HeartFilled className='size-6 text-red-800 md:size-5 dark:text-red-800' title='Remove' /> : <CruzIcon.Heart className='size-6 md:size-5' title='Save' />}
      </button>;
  };
  const DateSaved = () => {
    if (!showDateSaved || !v?.date_saved) return null;
    const {
      date_saved
    } = v || {};
    return <time dateTime={date_saved} className='block truncate text-sm font-normal md:text-xs'>
        Saved {dayjs(date_saved).fromNow()}
      </time>;
  };
  useEffect(() => {
    let t: NodeJS.Timeout;
    if (isNavigating) {
      t = setTimeout(() => setIsNavigating(false), 5000);
    }
    return () => clearTimeout(t);
  }, [isNavigating]);
  const NavOverlay = () => <div className='absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-white/20 backdrop-blur-[1px] dark:bg-black/20 transition-all duration-200'>
      <div className='rounded-full bg-white dark:bg-zinc-700 p-3 shadow'>
        <CruzIcon.Spin className='size-12 text-red-800 dark:text-sky-300 opacity-100' />
      </div>
    </div>;
  return <article className='relative' id={v._id as string}>
      {isNavigating && <NavOverlay />}
      <NextLink hrefLang='en' href={v?.detail_url || ''} className='block outline-none hover:opacity-90' onClick={() => setIsNavigating(true)}>
        <div>
          {SaveVehicle()}
          <CardPhoto photoUrl={v?.photo_preview_url} photoCount={showPhotoCount ? v?.photo_count : null} isDeleted={v?.post_status_id === -2} isSold={v?.sale_status_id === -1} />
          <h2 className='card-title m-0'>{v?.title}</h2>
          <div className='flex items-center justify-between'>
            <div className='mt-3 mb-2 flex w-fit items-center gap-x-2 truncate rounded-r-full bg-zinc-100 px-4 py-2 shadow-inner dark:bg-zinc-800'>
              <span className='truncate text-sm font-normal'>{v.condition_name}</span>
              <Mileage d={v?.mileage || 0} />
            </div>
            <Price v={v} />
          </div>
        </div>
        <section aria-label='Vehicle Summary'>
          <CardBody>
            <CardItem label='Estimated Value' value={<EstimatedValue d={v?.estimated_value} />} />
            {showEvRange && <CardItem label='Electric Range' value={EvRange()} />}
            {!_.includes(ignoreKeys, 'drive_type') && <CardItem label='Drive Type' value={v?.drive_type_name || '-'} />}
            <CardItem label='Engine' value={v?.engine?.name || '-'} />
            <CardItem label='Transmission' value={v?.transmission?.name || '-'} />
            {showAcceleration && <CardItem label={<>
                    Acceleration <span className='font-light'>(0-60 mph)</span>
                  </>} value={Acceleration()} />}
            {!_.includes(ignoreKeys, 'mpg') && <CardItem label={v?.engine?.is_ev ? 'MPGe' : 'MPG'} value={<MPG d={v?.mpg_list || []} />} />}
            <CardItem label='Max Seating' value={v?.specification?.max_seating || '-'} />
            <CardItem label='VIN' value={v?.vin || '-'} />
            {showStockNumber && <CardItem label='Stock Number' value={v?.reference?.stock_number || '-'} />}
          </CardBody>
        </section>
      </NextLink>
      {showUser && !_.isEmpty(v.user_display) && <UserInfo d={v?.user_display} hideLead={hideLead} />}
      {showUser && v?.location?.name && <DealerInfo d={v?.location as Vendor.Location} vehicle_id={v?._id as string || ''} vehicle_title={v?.title || ''} showRequestHistoryReport={_.includes([Vehicle.ConditionEnum.Used, Vehicle.ConditionEnum.Certified], v?.condition)} />}
      <Divider />
      <NextLink hrefLang='en' href={v?.detail_url || ''} className='block outline-none hover:opacity-90' onClick={() => setIsNavigating(true)}>
        <div className='flex items-center justify-between truncate px-4 py-2 font-light'>
          <section className='flex items-center gap-x-2 text-xs' aria-label='Vehicle statistics'>
            <span className='m-0 text-xs/4'>
              <span className='text-sm font-medium'>{(v?.view_count || 0).toLocaleString()}</span> view{(v?.view_count || 0) !== 1 && 's'}
            </span>
            <Bullet />
            <time dateTime={v.date_create} className='block truncate'>
              Listed {dayjs(v.date_create).fromNow()}
            </time>
          </section>
          <DateSaved />
        </div>
      </NextLink>
      {ToggleCompare}
    </article>;
}